import type { MeetingOccurrenceViewModel } from '@trinnvis/dabih-api-client';
import type { State } from 'src/store/types.js';
import {
  assetsNotDeleted,
  constitutionalDocumentsNotDeleted,
  contractsNotDeleted,
  currentEmployeeUuid,
  currentUserUuid,
  currentWeekRemainingTasksAndMeetingsForEmployee,
  documentsNotDeleted,
  employeeContractsAsListItems,
  employeeShortNamesAndIAsText,
  employeesNotDeleted,
  employeesShortNamesAndMe,
  eventOccurrencesNotDeleted,
  functionsNotDeleted,
  getContactsAsUsers,
  getIsInternalOrganization,
  getOrganization,
  getRecipientOptionsForIssues,
  getRiskAssessmentGroups,
  guidelinesNotDeleted,
  isCurrentUserEmployee,
  isIssueProcessed,
  isRestricted,
  issuesNotDeleted,
  key,
  leavePeriodsToday,
  meetingOccurrencesNotDeleted,
  partnersNotDeleted,
  reportsNotDeleted,
  riskAssessmentsNotDeleted,
  sector,
  singleUserVersion,
  type StartTaskInstance,
  startTasks,
  substancesNotDeleted,
  tasksNotDeleted,
  timekeepingPeriodsForCurrentEmployee,
  writeAccess,
} from 'src/store/selectors';
import { calculateUpdateStatus } from 'src/models/resolvers/calculate-update-status';
import { LocalDate, mondayThisWeek } from 'src/utilities/local-date';
import { calculateSicknessAbsenceStats } from 'src/utilities/sickness-absence-stats-selector';
import type { KpiSummary } from 'src/pages/status-page/kpi-summary';
import { orderBy, sortBy } from 'lodash';
import { dateTimeDisplayShort } from 'src/store/utilities';
import { capitalize, entityNameForLists, numberWord } from 'src/utilities/text';
import { calculateBalance } from 'src/utilities/time-balance/time-balance';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { getIssueLabel } from '../../store/selectors/issues/getIssueLabel';
import { getOrganizationId } from '../../store/selectors/organization/getOrganizationId';
import type { DocumentUpdatesForStatusPage } from '../../pages/status-page/document-updates-for-status-page';
import type { StartTasksForStatusPage } from '../../pages/status-page/start-tasks-for-status-page';
import type {
  FormItemForStatusPage,
  LeaderInterviewInfo,
  LeaderInterviewItem,
  StatusPageViewModel,
  WidgetContent,
} from '../../pages/status-page/status-page-view-model';
import type { ListSectionItemInput } from '../../library/lists/listSectionItemInput';
import type { BarGraphItem } from '../../library/elements/bar-graph-item';
import { BASE_PATH } from 'src/store';
import { meetingParticipantsText } from '../../store/meetingParticipantsText';
import { eventAssigneesText } from '../../store/eventAssigneesText';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { extractFormData, getFormPrompt } from 'src/store/selectors/form-functions';
import { userNamesAndMe } from 'src/store/selectors/user-names';
import type { FormElement } from '../../library/components/form';
import { unconfirmedLeavePeriods } from 'src/store/selectors/calendar/unconfirmed-leave-periods';
import { getFeatureStates } from 'src/store/selectors/features/getFeatureStates';
import { getUsersForAccess } from '../get-users-for-access';
import { t } from 'src/i18n';

interface ListSectionItemInputWithDate extends ListSectionItemInput {
  date: string;
}

interface Count {
  templateDeleted: number;
  templateUpdated: number;
  documentUpdated: number;
}
function documentLists(state: State) {
  const lists = [
    { type: 'assets', list: assetsNotDeleted(state) },
    { type: 'constitutionalDocuments', list: constitutionalDocumentsNotDeleted(state) },
    { type: 'contracts', list: contractsNotDeleted(state) },
    { type: 'documents', list: documentsNotDeleted(state) },
    { type: 'employees', list: employeesNotDeleted(state) },
    { type: 'eventOccurrences', list: eventOccurrencesNotDeleted(state) },
    { type: 'functions', list: functionsNotDeleted(state) },
    { type: 'guidelines', list: guidelinesNotDeleted(state) },
    { type: 'issues', list: issuesNotDeleted(state) },
    { type: 'meetingOccurrences', list: meetingOccurrencesNotDeleted(state) },
    { type: 'partners', list: partnersNotDeleted(state) },
    { type: 'reports', list: reportsNotDeleted(state) },
    { type: 'riskAssessments', list: riskAssessmentsNotDeleted(state) },
    { type: 'substances', list: substancesNotDeleted(state) },
    { type: 'tasks', list: tasksNotDeleted(state) },
  ];
  if (singleUserVersion(state)) {
    return lists.filter((l) => {
      return l.type !== 'functions' && l.type !== 'meetingOccurrences';
    });
  }
  return lists;
}

function getDocumentUpdates(state: State, hrefPrefix: string): DocumentUpdatesForStatusPage {
  const items: ListSectionItemInputWithDate[] = [];
  const count: Count = {
    templateDeleted: 0,
    templateUpdated: 0,
    documentUpdated: 0,
  };
  documentLists(state)
    .filter((x) => x.type !== 'partners')
    .forEach((type) => {
      type.list.forEach((item) => {
        const updateStatus = calculateUpdateStatus(item, LocalDate.now());
        if (
          updateStatus === 'templateDeleted' ||
          updateStatus === 'templateUpdated' ||
          updateStatus === 'documentUpdated'
        ) {
          let date = item[updateStatus];
          if (updateStatus === 'documentUpdated') {
            date = item.contentLastModified;
          }
          count[updateStatus] += 1;
          items.push({
            date,
            label: entityNameForLists(item.name, item.draftName, item.isConfirmedEntity),
            accessible: !isRestricted(item, currentUserUuid(state)),
            href: hrefPrefix + type.type + '/' + item.uuid,
            updateStatus,
          });
        }
      });
    });
  const sorted = orderBy(items, ['date'], ['desc']).map((item) => {
    return {
      label: LocalDate.fromString(item.date).toStringForDisplay() + ' ' + item.label,
      accessible: item.accessible,
      href: item.href,
      updateStatus: item.updateStatus,
    };
  });
  return {
    summary: [
      {
        label: t('status.updatedDocumentsCount', { count: count.documentUpdated || t('status.none') }),
        accessible: false,
        updateStatus: 'documentUpdated',
      },
      {
        label: t('status.suggestedUpdatesCount', { count: count.templateUpdated || t('status.none') }),
        accessible: false,
        updateStatus: 'templateUpdated',
      },
      {
        label: t('status.suggestedDeletionsCount', { count: count.templateDeleted || t('status.none') }),
        accessible: false,
        updateStatus: 'templateDeleted',
      },
    ],
    items: sorted,
  };
}

function getOverdueEvents(state: State, hrefPrefix: string): ListSectionItemInput[] {
  const today = LocalDate.now();
  return eventOccurrencesNotDeleted(state)
    .filter((e) => {
      return !e.doneDate && e.persistent && LocalDate.fromString(e.date).isBefore(today);
    })
    .map((e) => {
      return {
        label:
          LocalDate.fromString(e.date).toStringForDisplay() +
          ' ' +
          entityNameForLists(e.name, e.draftName, e.isConfirmedEntity),
        rightLabel: eventAssigneesText(
          e,
          functionsNotDeleted(state),
          employeesShortNamesAndMe(state),
          getContactsAsUsers(state),
        ),
        rightLabelClass: 'assignee',
        accessible: true,
        href: hrefPrefix + 'eventOccurrences/' + e.uuid,
      };
    });
}

function getDrafts(state: State, hrefPrefix: string): ListSectionItemInput[] {
  const items: ListSectionItemInputWithDate[] = [];
  documentLists(state).forEach((type) => {
    type.list.forEach((item) => {
      if (item.hasDraft) {
        const date = item.contentLastModified;
        items.push({
          date,
          label: entityNameForLists(item.name, item.draftName, item.isConfirmedEntity),
          accessible: !isRestricted(item, currentUserUuid(state)),
          href: hrefPrefix + type.type + '/' + item.uuid,
        });
      }
    });
  });
  return orderBy(items, ['date'], ['desc']).map((item) => {
    return {
      label: item.label,
      accessible: item.accessible,
      href: item.href,
    };
  });
}

function getIssues(state: State, hrefPrefix: string): ListSectionItemInput[] {
  const issues = orderBy(
    issuesNotDeleted(state)
      .filter((item) => {
        return !isIssueProcessed(item);
      })
      .map((item) => {
        const accessible = !isRestricted(item, currentUserUuid(state));
        return {
          date: item.reportedDate,
          label: getIssueLabel(item, accessible ?? false, employeesNotDeleted(state)),
          accessible,
          href: hrefPrefix + 'issues/' + item.uuid,
          locked: item.classification !== 'NONE',
        };
      }),
    ['date'],
    ['desc'],
  );
  return issues.map((item) => {
    delete item.date;
    return item;
  });
}

function getStartTasks(startTasks: StartTaskInstance[]): StartTasksForStatusPage {
  const done = startTasks.filter((t) => {
    return t.executed;
  });
  const remaining = startTasks.filter((t) => {
    return !t.executed;
  });
  let statusText: string;
  if (startTasks.length === done.length) {
    statusText = t('library.startTasks.allDoneAll');
  } else if (done.length === 0) {
    statusText = t('status.noStartTasksDone');
  } else {
    statusText = t('status.startTasksDoneCount', { done: done.length, total: startTasks.length });
  }
  return {
    total: startTasks.length,
    done: done.length,
    statusText,
    items: remaining.map((t) => {
      return {
        label: t.name,
        accessible: true,
        clickData: t.uuid,
      };
    }),
  };
}

function getFutureMeetings(state: State, hrefPrefix: string): ListSectionItemInput[] | undefined {
  if (singleUserVersion(state)) {
    return undefined;
  }
  const today = LocalDate.now();
  const futureMeetings = meetingOccurrencesNotDeleted(state).filter((m: MeetingOccurrenceViewModel): boolean => {
    const meetingDate = LocalDate.fromString(m.date);
    return (
      meetingDate.isSameOrAfter(today) &&
      meetingDate.isBefore(today.plusDays(3)) &&
      !m.name?.includes('Medarbeidersamtale')
    );
  });
  const sortedMeetings = sortBy(futureMeetings, ['date']);
  return sortedMeetings.map((m: MeetingOccurrenceViewModel): ListSectionItemInput => {
    return {
      label: LocalDate.fromString(m.date).toStringForDisplay() + ' ' + m.name,
      accessible: true,
      href: hrefPrefix + 'meetingOccurrences/' + m.uuid,
      rightLabel: meetingParticipantsText(
        m,
        functionsNotDeleted(state),
        employeesShortNamesAndMe(state),
        getContactsAsUsers(state),
        true,
      ),
      rightLabelClass: 'assignee',
    };
  });
}

// Get interview info for the current user only (for read-only users)
function getInterviewInfoForCurrentUser(
  state: State,
): { status: string; text: string; date?: string; time?: string; meetingUuid?: string }[] | undefined {
  if (singleUserVersion(state)) {
    return undefined;
  }

  const currentEmpUuid = currentEmployeeUuid(state);
  if (!currentEmpUuid) {
    return undefined;
  }

  const today = LocalDate.now();
  const result: { status: string; text: string; date?: string; time?: string; meetingUuid?: string }[] = [];

  // Get current employee's interviews
  const currentEmployee = employeesNotDeleted(state).find((e) => e.uuid === currentEmpUuid && e.status === 'ACTIVE');
  if (!currentEmployee) {
    return result;
  }

  const interviews = meetingOccurrencesNotDeleted(state)
    .filter((m) => m.name?.includes('Medarbeidersamtale') && m.participants.includes(currentEmpUuid))
    .sort((a, b) => b.date.localeCompare(a.date)); // Sort by date descending

  if (interviews.length === 0) {
    result.push({
      status: 'alert',
      text: t('status.neverHadInterview'),
    });
    return result;
  }

  // Find next or most recent interview
  const futureInterview = interviews.find((i) => LocalDate.fromString(i.date).isSameOrAfter(today));
  const mostRecentInterview = interviews[0]; // Already sorted by date descending

  if (futureInterview) {
    // Has upcoming interview
    result.push({
      status: 'checked',
      text: t('status.nextInterview'),
      date: futureInterview.date,
      time: futureInterview.time,
      meetingUuid: futureInterview.uuid,
    });
  } else {
    // Check if most recent is within a year
    const lastInterviewDate = LocalDate.fromString(mostRecentInterview.date);
    const oneYearAgo = today.minusDays(366);
    if (lastInterviewDate.isSameOrAfter(oneYearAgo)) {
      result.push({
        status: 'checked',
        text: t('status.lastInterview'),
        date: mostRecentInterview.date,
        time: mostRecentInterview.time,
        meetingUuid: mostRecentInterview.uuid,
      });
    } else {
      result.push({
        status: 'alert',
        text: t('status.noInterviewOverAYear'),
        date: mostRecentInterview.date,
        time: mostRecentInterview.time,
        meetingUuid: mostRecentInterview.uuid,
      });
    }
  }

  return result;
}

function getInterviewInfo(
  state: State,
): { status: string; text: string; date?: string; meetingUuid?: string }[] | undefined {
  if (singleUserVersion(state)) {
    return undefined;
  }
  const result: { status: string; text: string; date?: string; meetingUuid?: string }[] = [];
  const today = LocalDate.now();
  const currentEmpUuid = currentEmployeeUuid(state);

  const employeesInterviewState = employeesNotDeleted(state)
    .filter((e) => {
      return e.name !== '' && e.status === 'ACTIVE';
    })
    .map((e) => {
      const interviews = meetingOccurrencesNotDeleted(state).filter((m) => {
        return m.name?.includes('Medarbeidersamtale') && m.participants.includes(e.uuid);
      });
      if (!interviews.length) {
        return { uuid: e.uuid, state: 'none', interviews: [] };
      }
      const futureOrRecent = interviews.find((i) => {
        const day = LocalDate.fromString(i.date);
        return day.isSameOrAfter(today) || day.isSameOrAfter(today.minusDays(366));
      });
      if (futureOrRecent) {
        return { uuid: e.uuid, state: 'ok', interviews };
      }
      return { uuid: e.uuid, state: 'overdue', interviews };
    });
  const overdue = employeesInterviewState.filter((e) => {
    return e.state === 'overdue';
  });
  const ok = employeesInterviewState.filter((e) => {
    return e.state === 'ok';
  });

  // Handle OK employees
  if (ok.length) {
    const uuids = ok.map((n) => {
      return n.uuid;
    });
    const resultItem: { status: string; text: string; date?: string; meetingUuid?: string } = {
      status: 'checked',
      text: capitalize(t('status.namesHadInterviewLastYear', { names: employeeShortNamesAndIAsText(uuids, state) })),
    };

    // Add date and UUID for current user's interview if they're in the OK list
    if (currentEmpUuid && uuids.includes(currentEmpUuid)) {
      const currentEmpState = ok.find((e) => e.uuid === currentEmpUuid);
      if (currentEmpState && currentEmpState.interviews.length > 0) {
        // Find the most recent or upcoming interview
        const sortedInterviews = orderBy(currentEmpState.interviews, ['date'], ['desc']);
        const nextInterview = sortedInterviews.find((i) => LocalDate.fromString(i.date).isSameOrAfter(today));
        const mostRecent = nextInterview || sortedInterviews[0];
        if (mostRecent) {
          resultItem.date = mostRecent.date;
          resultItem.meetingUuid = mostRecent.uuid;
        }
      }
    }
    result.push(resultItem);
  }

  // Handle overdue employees
  if (overdue.length) {
    const uuids = overdue.map((n) => {
      return n.uuid;
    });
    const resultItem: { status: string; text: string; date?: string; meetingUuid?: string } = {
      status: 'alert',
      text: capitalize(t('status.namesNoInterviewOverAYear', { names: employeeShortNamesAndIAsText(uuids, state) })),
    };

    // Add date and UUID for current user's interview if they're in the overdue list
    if (currentEmpUuid && uuids.includes(currentEmpUuid)) {
      const currentEmpState = overdue.find((e) => e.uuid === currentEmpUuid);
      if (currentEmpState && currentEmpState.interviews.length > 0) {
        // Find the most recent interview
        const sortedInterviews = orderBy(currentEmpState.interviews, ['date'], ['desc']);
        const mostRecent = sortedInterviews[0];
        if (mostRecent) {
          resultItem.date = mostRecent.date;
          resultItem.meetingUuid = mostRecent.uuid;
        }
      }
    }
    result.push(resultItem);
  }

  // Handle employees with no interviews
  const none = employeesInterviewState.filter((e) => {
    return e.state === 'none';
  });
  if (none.length) {
    const uuids = none.map((n) => {
      return n.uuid;
    });
    result.push({
      status: 'alert',
      text: capitalize(t('status.namesNeverHadInterview', { names: employeeShortNamesAndIAsText(uuids, state) })),
    });
  }
  return result;
}

// Get leader interview info for performance review-only accounts
function getLeaderInterviewInfo(state: State): LeaderInterviewInfo | undefined {
  if (singleUserVersion(state)) {
    return undefined;
  }

  const today = LocalDate.now();
  const currentUserUuid = currentEmployeeUuid(state);
  if (!currentUserUuid) {
    return undefined;
  }

  const withCurrentUser: LeaderInterviewItem[] = [];
  const withoutCurrentUser: LeaderInterviewItem[] = [];
  const notPlanned: { employeeUuid: string; employeeName: string }[] = [];

  // Get functions where current user is assigned (for checking responsible function)
  const currentUserFunctions = functionsNotDeleted(state)
    .filter((f) => f.employees.includes(currentUserUuid))
    .map((f) => f.uuid);

  // Get users with their access levels
  const usersWithAccess = getUsersForAccess(state);

  // Get employees who do NOT have write access (only READONLY and NONE)
  // Write access = ADMIN or USER, so we exclude those
  const employeesWithoutWriteAccess = usersWithAccess
    .filter((u) => u.accessLevel !== 'ADMIN' && u.accessLevel !== 'USER')
    .map((u) => u.uuid);

  // Get all active employees who don't have write access
  const employees = employeesNotDeleted(state).filter((e) => {
    return e.name !== '' && e.status === 'ACTIVE' && employeesWithoutWriteAccess.includes(e.uuid);
  });

  employees.forEach((employee) => {
    // Get all performance review meetings for this employee
    const interviews = meetingOccurrencesNotDeleted(state)
      .filter((m) => m.name?.includes('Medarbeidersamtale') && m.participants.includes(employee.uuid))
      .sort((a, b) => LocalDate.fromString(a.date).compareTo(LocalDate.fromString(b.date)));

    // Get interviews planned or within last year
    const futureOrRecentInterviews = interviews.filter((i) => {
      const interviewDate = LocalDate.fromString(i.date);
      return interviewDate.isSameOrAfter(today) || interviewDate.isSameOrAfter(today.minusDays(366));
    });

    if (futureOrRecentInterviews.length === 0) {
      // Employee has no planned interviews or interviews within the last year
      notPlanned.push({
        employeeUuid: employee.uuid,
        employeeName: employee.firstName + ' ' + employee.lastName,
      });
    } else {
      // Find if there's any meeting where current user (leader) is participant or responsible
      const meetingWithLeader = futureOrRecentInterviews.find((i) => {
        // Check if leader is in participants
        const isParticipant = i.participants.includes(currentUserUuid);

        // Check if leader is responsible employee
        const isResponsibleEmployee = i.responsibleEmployeeUuid === currentUserUuid;

        // Check if leader's function is responsible
        const isResponsibleFunction = i.responsibleUuid && currentUserFunctions.includes(i.responsibleUuid);

        return isParticipant || isResponsibleEmployee || isResponsibleFunction;
      });

      if (meetingWithLeader) {
        // Use the meeting where leader is participant or responsible
        withCurrentUser.push({
          employeeUuid: employee.uuid,
          employeeName: employee.firstName + ' ' + employee.lastName,
          status: 'checked',
          date: meetingWithLeader.date,
          time: meetingWithLeader.time,
          meetingUuid: meetingWithLeader.uuid,
        });
      } else {
        // No meeting with leader, use the most recent/upcoming meeting
        const latestMeeting = futureOrRecentInterviews[0];
        withoutCurrentUser.push({
          employeeUuid: employee.uuid,
          employeeName: employee.firstName + ' ' + employee.lastName,
          status: 'checked',
          date: latestMeeting.date,
          time: latestMeeting.time,
          meetingUuid: latestMeeting.uuid,
        });
      }
    }
  });

  // Sort meetings by date
  withCurrentUser.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  withoutCurrentUser.sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));

  return {
    withCurrentUser: withCurrentUser.length > 0 ? withCurrentUser : undefined,
    withoutCurrentUser: withoutCurrentUser.length > 0 ? withoutCurrentUser : undefined,
    notPlanned: notPlanned.length > 0 ? notPlanned : undefined,
  };
}

const periodText = (): string => {
  const today = LocalDate.now();
  if (mondayThisWeek().isSame(today)) {
    return t('status.thisWeek');
  }
  return t('status.restOfThisWeek');
};

function getForms(state: State): FormItemForStatusPage[] {
  const result = reportsNotDeleted(state)
    .filter((r) => {
      return r.isForm;
    })
    .map((r) => {
      return {
        uuid: r.uuid,
        name: r.name ?? '',
        deadline: r.formResponseDeadline ?? '',
        expired:
          r.formResponseDeadline !== undefined &&
          r.formResponseDeadline !== '' &&
          LocalDate.fromString(r.formResponseDeadline).isBefore(LocalDate.fromString()),
        isUserRespondent:
          r.formRespondentEmployees !== undefined &&
          r.formRespondentEmployees.length > 0 &&
          r.formRespondentEmployees.includes(currentEmployeeUuid(state) ?? ''),
        answered:
          extractFormData((r.formElements ?? []) as FormElement[], currentUserUuid(state)).currentUserResponseCount > 0,
        prompt: getFormPrompt(
          state,
          r.formResponsibleFunction ?? '',
          r.formResponsibleEmployees ?? [],
          r.formResponsibleContacts ?? [],
          r.formResponseDeadline ?? '',
        ),
        isSurvey: extractFormData((r.formElements ?? []) as FormElement[]).respondentMultipleFieldsCount > 0,
        anonymous: r.anonymousForm ?? false,
        item: {
          employee: r.employeeUuid ?? '',
          partnerContactUuid: r.partnerContactUuid ?? '',
          pages: (r.pages ?? []).map((x) => '' + x),
          reportDate: r.reportDate ?? '',
          accessControl: r.accessControl ?? [],
          classification: r.classification ?? 'NONE',
          content: r.content ?? '',
          name: r.name ?? '',
          isForm: r.isForm,
          formSubtitle: r.formSubtitle,
          formResponseDeadline: r.formResponseDeadline,
          formResponsibleFunction: r.formResponsibleFunction,
          formResponsibleEmployees: r.formResponsibleEmployees,
          formResponsibleContacts: r.formResponsibleContacts,
          formRespondentEmployees: r.formRespondentEmployees,
          formRespondentContacts: r.formRespondentContacts,
          elements: (r.formElements ?? []) as FormElement[],
        },
      };
    });
  return sortBy(result, [(f) => (f.answered ? 1 : 0), (f) => f.deadline, (f) => f.name]);
}

function getCurrentEmployeeInfo(state: State): WidgetContent[] | undefined {
  if (singleUserVersion(state) || !isCurrentUserEmployee(state)) {
    return undefined;
  }
  const result: WidgetContent[] = [];
  const organizationId = getOrganizationId(state);
  const employeeUuid = currentEmployeeUuid(state) ?? '';

  const forms = getForms(state).filter((form) => {
    return !form.expired && form.isUserRespondent;
  });
  if (forms.length) {
    forms.forEach((form) => {
      if (form.answered) {
        result.push({
          text: t('status.formStillOpen'),
          class: 'info',
        });
      } else {
        result.push({
          text: form.prompt,
          class: 'alert',
        });
      }
      result.push({
        text: form.name ?? '',
        href: '/account/' + organizationId + '/' + pageIds.status + '?dialog=form&report=' + form.uuid,
      });
    });
  }

  const eventsCount = currentWeekRemainingTasksAndMeetingsForEmployee(state);
  const period = periodText();
  let eventsCountText = t('status.noTasksPeriod', { period });
  if (eventsCount > 0) {
    eventsCountText = t('status.oneTaskPeriod', { period });
    if (eventsCount > 1) {
      eventsCountText = t('status.tasksCountPeriod', { count: capitalize(numberWord(eventsCount)), period });
    }
  }
  result.push({
    text: eventsCountText,
    class: '',
  });
  result.push({
    text: t('status.goToWeeklyPlan'),
    href: '/account/' + organizationId + '/' + pageIds.events,
  });
  const functions = functionsNotDeleted(state).filter((f) => {
    return f.employees.includes(employeeUuid);
  });
  let functionCountText = t('status.oneFunction');
  if (functions.length > 1) {
    functionCountText = t('status.functionsCount', { count: capitalize(numberWord(functions.length)) });
  }
  result.push({
    text: functionCountText,
    class: 'checked',
  });
  const contracts = employeeContractsAsListItems(state, '', employeeUuid).filter((item) => {
    return item.mainIcon !== 'suggestion';
  });
  if (contracts) {
    let contractsCountText = t('status.oneContract');
    if (contracts.length > 1) {
      contractsCountText = t('status.contractsCount', { count: capitalize(numberWord(contracts.length)) });
    }
    result.push({
      text: contractsCountText,
      class: 'checked',
    });
  }
  if (getFeatureStates(state).staffing) {
    const employee = getOrganization(state)?.employeesById[employeeUuid];
    const employmentStartDate = employee?.employmentStartDate;
    const employmentEndDate = employee?.employmentEndDate;
    const balance = calculateBalance(
      timekeepingPeriodsForCurrentEmployee(state),
      employmentStartDate,
      employmentEndDate,
    );
    const balanceDecimalsTruncated = Math.round(balance * 10) / 10;
    result.push({
      text: t('status.timeOffHours', { count: balanceDecimalsTruncated }),
      class: balanceDecimalsTruncated < 0 ? 'alert' : 'checked',
    });
  }
  result.push({
    text: t('status.goToMyStaffInfo'),
    href: '/account/' + organizationId + '/695944/employees/' + employeeUuid,
  });
  return result;
}

function getFunctionsInfo(state: State, hrefPrefix: string): ListSectionItemInput[] | undefined {
  if (singleUserVersion(state)) {
    return undefined;
  }
  const unassignedFunctions = functionsNotDeleted(state).filter((f) => {
    return f.employees.length === 0;
  });
  if (unassignedFunctions.length) {
    return unassignedFunctions.map((f) => {
      return {
        label: t('status.functionNotAssigned', { name: f.name }),
        mainIcon: 'alert',
        href: hrefPrefix + 'functions/' + f.uuid,
        accessible: true,
      };
    });
  }
  return [
    {
      label: t('status.allFunctionsStaffed'),
      mainIcon: 'checkBlue',
      accessible: true,
    },
  ];
}

function getRiskData(state: State): { label: string; items: BarGraphItem[] }[] {
  const result = getRiskAssessmentGroups(state, '')
    .filter((r) => {
      return r.items.length > 0;
    })
    .map((r) => {
      const risk4 = r.items.filter((item) => {
        return item.mainIcon === 'risk4';
      });
      const risk3 = r.items.filter((item) => {
        return item.mainIcon === 'risk3';
      });
      const risk2 = r.items.filter((item) => {
        return item.mainIcon === 'risk2';
      });
      const risk1 = r.items.filter((item) => {
        return item.mainIcon === 'risk1';
      });
      const risk0 = r.items.filter((item) => {
        return (
          item.mainIcon !== 'risk4' &&
          item.mainIcon !== 'risk3' &&
          item.mainIcon !== 'risk2' &&
          item.mainIcon !== 'risk1'
        );
      });
      return {
        label: r.themeName,
        items: [
          { count: risk4.length, color: 'var(--risk-color-4)', label: '' },
          { count: risk3.length, color: 'var(--risk-color-3)', label: '' },
          { count: risk2.length, color: 'var(--risk-color-2)', label: '' },
          { count: risk1.length, color: 'var(--risk-color-1)', label: '' },
          { count: risk0.length, color: 'var(--backgroundGray)', label: '' },
        ],
      };
    });
  let max = 0;
  result.forEach((r) => {
    let running = 0;
    r.items.forEach((i) => {
      running += i.count;
    });
    if (running > max) {
      max = running;
    }
  });
  console.log('max', max);
  return result.map((r) => {
    const items: BarGraphItem[] = r.items;
    let total = 0;
    items.forEach((i) => {
      total += i.count;
    });
    if (total < max) {
      items.push({
        label: '',
        count: max - total,
        color: 'var(--backgroundGray)',
      });
    }
    return {
      label: r.label,
      items,
    };
  });
}

const bhtPartnerNames = ['PlussBHT', 'MDCO'];

function getHealthcareStatus(state: State): ListSectionItemInput[] {
  const newVar: ListSectionItemInput[] = [];

  const bhtPartner = partnersNotDeleted(state).find((x) => bhtPartnerNames.includes(x.name ?? ''));
  const partnerName = bhtPartner?.name ?? t('status.bhtSupplierFallback');

  const contract = contractsNotDeleted(state).find((f) => f.name === 'Avtale om BHT');
  if (contract) {
    const signingOrders = contract.signingOrders ?? [];
    if (signingOrders.length === 0) {
      newVar.push({
        label: t('status.healthcareOrderedFrom', { partner: partnerName }),
        mainIcon: 'checkBlue',
        accessible: false,
      });
      newVar.push({
        label: t('status.agreementSentForSigning'),
        mainIcon: 'info',
        accessible: false,
      });
    } else {
      const s = signingOrders.find((f) => f.signingOrderStatus === 'OPEN');
      const e = employeesNotDeleted(state).map((e2) => e2.uuid);
      if (s !== undefined) {
        newVar.push({
          label: t('status.healthcareOrderedFrom', { partner: partnerName }),
          mainIcon: 'checkBlue',
          accessible: false,
        });
        const n = s.signatories.find((r) => e.includes(r.uuid));
        if (n !== undefined) {
          if (n.signatoryStatus === 'OPEN') {
            const em = employeesNotDeleted(state).find((e2) => e2.uuid === n.uuid);
            if (em) {
              newVar.push({
                label: t('status.agreementSentToForSigning', { name: em.name }),
                mainIcon: 'info',
                accessible: false,
              });
            }
          } else if (n.signatoryStatus === 'SIGNED') {
            newVar.push({
              label: t('status.agreementWaitingForSigningFrom', { partner: partnerName }),
              mainIcon: 'info',
              accessible: false,
            });
          }
        }
      } else {
        if (bhtPartner !== undefined) {
          newVar.push({
            label: partnerName,
            mainIcon: 'checkBlue',
            accessible: true,
            href: '695944/partners/' + bhtPartner.uuid,
          });
        }
      }
    }
  } else {
    newVar.push({
      label: t('status.healthcareOrderedFrom', { partner: partnerName }),
      mainIcon: 'checkBlue',
      accessible: false,
    });
  }

  const meeting = orderBy(
    meetingOccurrencesNotDeleted(state).filter((f) => f.name === 'HMS-gjennomgang'),
    [(x) => x.date],
    ['desc'],
  )[0];
  if (meeting) {
    newVar.push({
      label: t('status.hseReview', { date: dateTimeDisplayShort(meeting.date, undefined) }),
      mainIcon: 'info',
      accessible: true,
      href: '695944/meetingOccurrences/' + meeting.uuid,
    });
  } else {
    newVar.push({
      label: t('status.partnerWillContactForHseReview', { partner: partnerName }),
      mainIcon: 'info',
      accessible: false,
    });
  }
  return newVar;
}

/**
 * Beregner HMS-KPI-sammendrag for ledelsesdashbordet (issue #13276, krav 1.7.2).
 * Gjenbruker eksisterende selectors for avvik og sykefravær.
 */
export function getKpiSummary(state: State): KpiSummary {
  const today = LocalDate.fromString(state.today);
  const periodStart = today.minusDays(365);
  const issues = issuesNotDeleted(state);

  const openIssues = issues.filter((i) => !isIssueProcessed(i)).length;

  const newIssuesLast12Months = issues.filter((i) => {
    if (!i.reportedDate) return false;
    return LocalDate.fromString(i.reportedDate).isSameOrAfter(periodStart);
  }).length;

  const closingDays: number[] = [];
  let closedIssuesLast12Months = 0;
  for (const i of issues) {
    if (!isIssueProcessed(i) || !i.reportedDate || !i.processedDate) continue;
    const processed = LocalDate.fromString(i.processedDate);
    if (!processed.isSameOrAfter(periodStart)) continue;
    closedIssuesLast12Months++;
    const days = LocalDate.fromString(i.reportedDate).until(processed);
    if (days >= 0) closingDays.push(days);
  }
  const averageClosingDays =
    closingDays.length > 0 ? Math.round(closingDays.reduce((a, b) => a + b, 0) / closingDays.length) : null;

  let sickLeaveYearName = '';
  let sickLeavePercent: number | null = null;
  let sickLeavePercentTotal: number | null = null;
  const stats = calculateSicknessAbsenceStats(state);
  if (stats.genderDefined && stats.years.length > 0) {
    const latest = stats.years[stats.years.length - 1];
    sickLeaveYearName = latest.yearName;
    if (latest.workDays > 0) {
      sickLeavePercent = Math.round((latest.absenceDays / latest.workDays) * 1000) / 10;
      sickLeavePercentTotal = Math.round((latest.absenceDaysTotal / latest.workDays) * 1000) / 10;
    }
  }

  return {
    openIssues,
    newIssuesLast12Months,
    averageClosingDays,
    closedIssuesLast12Months,
    sickLeaveYearName,
    sickLeavePercent,
    sickLeavePercentTotal,
  };
}

export function statusPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): StatusPageViewModel {
  const organization = getOrganization(state);
  if (organization === undefined) {
    throw new Error('Illegal state (E154), organization not found');
  }
  const organizationId = getOrganizationId(state);
  const currentEmployeeInfo = getCurrentEmployeeInfo(state);

  const recipientOptionsForIssues = getRecipientOptionsForIssues(state);
  if (recipientOptionsForIssues.length === 0) {
    const employeeUuid = currentEmployeeUuid(state) ?? '';

    if (recipientOptionsForIssues.find((x) => x.value === employeeUuid) === undefined) {
      recipientOptionsForIssues.push({
        value: employeeUuid,
        text: t('status.me'),
      });
    }
  }

  return {
    ...viewModel,
    type: 'status-page',
    icon: 'robot',
    href: hrefPrefix,
    key: key(state),
    pdfHref: BASE_PATH + '/organizations/' + organizationId + '/substances/substances-list.pdf?key=' + key(state),
    today: LocalDate.fromString(state.today),
    sector: sector(state),
    isInternalOrganization: getIsInternalOrganization(state),
    leavePeriodsToday: singleUserVersion(state) ? undefined : leavePeriodsToday(state),
    unconfirmedLeavePeriods: unconfirmedLeavePeriods(state),
    overdueEvents: getOverdueEvents(state, hrefPrefix),
    drafts: getDrafts(state, hrefPrefix),
    issues: getIssues(state, hrefPrefix),
    documentUpdates: getDocumentUpdates(state, hrefPrefix),
    startTasks: getStartTasks(startTasks(state)),
    futureMeetings: getFutureMeetings(state, hrefPrefix),
    interviewInfo: writeAccess(state) ? getInterviewInfo(state) : getInterviewInfoForCurrentUser(state),
    leaderInterviewInfo:
      getFeatureStates(state).performanceReview && writeAccess(state) ? getLeaderInterviewInfo(state) : undefined,
    currentEmployeeInfo: currentEmployeeInfo,
    functionsInfo: getFunctionsInfo(state, hrefPrefix),
    riskGroups: getRiskData(state),
    kpis: getFeatureStates(state).managementDashboard ? getKpiSummary(state) : undefined,
    ownerEmail: organization.ownerEmail,
    healthcareSignatoryOptions: recipientOptionsForIssues,
    healthcareStatus: getHealthcareStatus(state),
    norengrosStatus: [],
    forms: getForms(state),
    userNames: userNamesAndMe(state),
    leavePeriodEditRestriction: organization.leavePeriodEditRestriction,
  };
}
